import { FormattedMessage, useIntl } from "react-intl";
import { Group, Stack, Title } from "@mantine/core";
import { EstimatedPeriodBillCard, EstimatedTotalBillCard } from "../../components/EstimatedTotalBillCard.js";
import { useProductIds } from "../../../../hooks/subscription.js";
import { DATE_RANGE_PRESET } from "../constant/dateRange.js";
import { useUsageControlsProps } from "../hooks/propGenerators.js";
import { UsageControls } from "../UsageControls.js";
import { useAllDailyCosts } from "../hooks/fetchDailyCosts.js";
import { TIERED_PRODUCT_IDS } from "@project/common";
import { StackedChart } from "./StackedChart.js";
import { useAllUsageSums } from "../../components/hooks/fetchSums.js";
import { CombinedSpendTable } from "./CombinedTable.js";
import { isFeatureEnabled } from "../../../../utils/posthog.js";
import { AddonsTable } from "./AddonsTable.js";
import { useIsServiceAccount } from "../../../../hooks/user.js";
import { useIsDeveloperPartner } from "../../../../hooks/user.js";

function SpendByMetric() {
  const usageControlsProps = useUsageControlsProps();
  const isServiceAccount = useIsServiceAccount();
  const isDeveloperPartner = useIsDeveloperPartner();
  const isSubscriptionAddonsEnabled = isFeatureEnabled("enable-subscription-addons");

  const {
    dateRange: [from, to],
    timeInterval: interval,
    metricsInDetailedView,
    apiKeyId,
  } = usageControlsProps;

  const metrics = metricsInDetailedView.length > 0
    ? metricsInDetailedView
    : TIERED_PRODUCT_IDS;

  const apiParams = { from, to, interval, apiKeyId };

  const {
    allDailyCosts,
    isLoadingDailyCosts,
    isErrorDailyCosts,
  } = useAllDailyCosts(apiParams);

  const {
    usageSums,
    isLoading: isLoadingUsageSums,
    isError: isErrorUsageSums,
  } = useAllUsageSums(apiParams);

  return (
    <Stack gap="lg" pb="lg">
      <Title order={2}>
        <FormattedMessage defaultMessage="Spend by metric" id="stackedChart.title" />
      </Title>

      <UsageControls {...usageControlsProps} />

      <StackedChart
        metrics={metrics}
        allDailyCosts={allDailyCosts}
        isLoadingDailyCosts={isLoadingDailyCosts}
        isErrorDailyCosts={isErrorDailyCosts}
      />

      <CombinedSpendTable
        metrics={metrics}
        usageSums={usageSums}
        isLoadingUsageSums={isLoadingUsageSums}
        isErrorUsageSums={isErrorUsageSums}
      />

      {(!isServiceAccount && !isDeveloperPartner && isSubscriptionAddonsEnabled) && (
        <AddonsTable />
      )}
    </Stack>
  );
}

export function SpendTab() {
  const intl = useIntl();
  const { lastPeriod } = DATE_RANGE_PRESET;
  const [from, to] = lastPeriod.dateRange;
  const { isLoadingSubscriptions, tieredProductIds } = useProductIds();

  return (
    <Stack gap="xl">
      <Group gap={24}>
        <EstimatedTotalBillCard
          showButton={false}
          productIds={tieredProductIds}
          isLoadingSubscriptions={isLoadingSubscriptions}
        />
        <EstimatedPeriodBillCard
          from={from}
          to={to}
          productIds={tieredProductIds}
          isLoadingSubscriptions={isLoadingSubscriptions}
          periodTitle={intl.formatMessage(lastPeriod.message)}
        />
      </Group>

      <SpendByMetric />
    </Stack>
  );
}
